// forms/wasteDisposalForm/actions.js

// Broadcast handler model imports
import {
  WasteDisposalContainerInfo,
  WasteDisposalConstituents,
} from "../../../models/WasteDisposal/WasteDisposal";

// Basic Info actions
import { makeWasteDisposalFormBasicInfoActions } from "./basicInfo/wasteDisposalFormBasicInfoActions";
// Waste Info actions
import { makeWasteDisposalFormWasteInfoActions } from "./wasteInfo/wasteDisposalFormActions";
// Paper Work actions
import { makeWasteDisposalFormPaperWorkActions } from "./paperWork/wasteDisposalFormPaperWorkActions";

// --- Broadcast Utilities ---

/**
 * Convert snake_case to camelCase
 * @param {string} str - Snake case string (e.g., "client_name")
 * @returns {string} - Camel case string (e.g., "clientName")
 */
const toCamelCase = (str) =>
  str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

/**
 * Apply a snake_case patch to a camelCase model instance.
 * Tries setter methods first, falls back to direct property assignment.
 * @param {Object} target - Model instance to update
 * @param {Object} patch - Patch data with snake_case keys
 * @param {string[]} skipKeys - Keys to skip (e.g., ["id", "action", "patch_type"])
 */
const applyPatchToObject = (target, patch, skipKeys = ["id", "action"]) => {
  Object.entries(patch).forEach(([key, value]) => {
    if (skipKeys.includes(key)) return;
    const camelKey = toCamelCase(key);
    const setterName = `set${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
    if (typeof target[setterName] === "function") {
      target[setterName](value);
    } else if (camelKey in target) {
      target[camelKey] = value;
    }
  });
};

// Helper functions
const makeHelpers = (set, get, tabKey) => ({
  bump: (patch) => {
    const s = get();
    const prev = s[tabKey] || {};
    const nextRev = (prev.rev || 0) + 1;
    set({ [tabKey]: { ...prev, rev: nextRev, ...(patch || {}) } });
  },
  setWasteDisposalUi: (patch) => {
    const s = get();
    const curr = s[tabKey];
    const prevUI = curr?.delForm?.wasteDisposalFormUI || {};
    const nextUI =
      typeof patch === "function"
        ? patch(prevUI)
        : Object.keys(patch).reduce(
            (acc, key) => {
              const val = patch[key];
              if (typeof val === "function") {
                acc[key] = val(prevUI[key]);
              } else if (key === "core") {
                acc[key] =
                  typeof val === "function"
                    ? val(prevUI[key])
                    : { ...(prevUI[key] || {}), ...val };
              } else {
                acc[key] = { ...(prevUI[key] || {}), ...val };
              }
              return acc;
            },
            { ...prevUI }
          );
    set({
      [tabKey]: {
        ...curr,
        delForm: { ...curr.delForm, wasteDisposalFormUI: nextUI },
      },
    });
  },
  // Helper to wrap Waste Disposal patches for routing through DEL service
  // Wraps patch as: { patch_type: "waste_disposal", waste_disposal_data: {...} }
  // Injects del_id from current DEL state for database linking
  emitWdPatch: (patch, emitPatch) => {
    if (!emitPatch || !patch) return;

    // Get the current DEL ID from store state
    const s = get();
    const delId = s[tabKey]?.currentDel?.id;

    // Inject del_id into the patch for database linking
    const patchWithDelId = {
      ...patch,
      del_id: delId,
    };

    const wrappedPatch = {
      patch_type: "waste_disposal",
      waste_disposal_data: patchWithDelId,
    };
    emitPatch(wrappedPatch);
  },
});

export const makeWasteDisposalFormActions = (set, get, tabKey) => {
  const h = makeHelpers(set, get, tabKey);

  // --- Broadcast Handlers ---

  /**
   * Handle container row broadcast (add/update/remove)
   * @param {WasteDisposal} wasteDisposal - The waste disposal model instance
   * @param {Object} patch - { id, action, container?, amount?, waste_type? }
   */
  const handleContainerBroadcast = (wasteDisposal, patch) => {
    const { id, action } = patch;

    if (action === "add") {
      // Check for duplicate before adding
      const exists = wasteDisposal.containerInfo.some((c) => c.rowId === id);
      if (!exists) {
        const newContainer = new WasteDisposalContainerInfo(patch);
        wasteDisposal.containerInfo.push(newContainer);
      }
      return;
    }

    if (action === "remove") {
      const idx = wasteDisposal.containerInfo.findIndex((c) => c.rowId === id);
      if (idx >= 0) wasteDisposal.containerInfo.splice(idx, 1);
      return;
    }

    // Update existing or add if not found
    const container = wasteDisposal.containerInfo.find((c) => c.rowId === id);
    if (container) {
      applyPatchToObject(container, patch, [
        "id",
        "action",
        "patch_type",
        "waste_disposal_id",
      ]);
    } else {
      // Row doesn't exist locally - add it
      const newContainer = new WasteDisposalContainerInfo(patch);
      wasteDisposal.containerInfo.push(newContainer);
    }
  };

  /**
   * Handle constituent row broadcast (add/update/remove)
   * @param {WasteDisposal} wasteDisposal - The waste disposal model instance
   * @param {Object} patch - { id, action, constituent?, concentration_range? }
   */
  const handleConstituentBroadcast = (wasteDisposal, patch) => {
    const { id, action } = patch;

    if (action === "add") {
      // Check for duplicate before adding
      const exists = wasteDisposal.constituents.some((c) => c.rowId === id);
      if (!exists) {
        const newConstituent = new WasteDisposalConstituents(patch);
        wasteDisposal.constituents.push(newConstituent);
      }
      return;
    }

    if (action === "remove") {
      const idx = wasteDisposal.constituents.findIndex((c) => c.rowId === id);
      if (idx >= 0) wasteDisposal.constituents.splice(idx, 1);
      return;
    }

    // Update existing or add if not found
    const constituent = wasteDisposal.constituents.find((c) => c.rowId === id);
    if (constituent) {
      applyPatchToObject(constituent, patch, [
        "id",
        "action",
        "patch_type",
        "waste_disposal_id",
      ]);
    } else {
      // Row doesn't exist locally - add it
      const newConstituent = new WasteDisposalConstituents(patch);
      wasteDisposal.constituents.push(newConstituent);
    }
  };

  /**
   * Main entry point for incoming Waste Disposal broadcasts.
   * Called by useDelAutosave hook when del_waste_disposal_update event received.
   * Hook calls bump() after this returns.
   * @param {Object} patchData - { patch_type, waste_disposal_id, ...fields }
   */
  const handleWasteDisposalBroadcast = (patchData) => {
    const state = get();
    const del = state[tabKey]?.currentDel;
    if (!del || !del.wasteDisposal) {
      console.warn(
        "handleWasteDisposalBroadcast: No Waste Disposal found on currentDel"
      );
      return;
    }

    const wasteDisposal = del.wasteDisposal;
    const { patch_type, waste_disposal_id } = patchData;

    // Verify this broadcast is for our waste disposal
    if (wasteDisposal.basicInfo?.id !== waste_disposal_id) {
      return;
    }

    switch (patch_type) {
      case "waste_disposal":
        // Basic info field updates - apply to basicInfo object
        applyPatchToObject(wasteDisposal.basicInfo, patchData, [
          "patch_type",
          "waste_disposal_id",
          "id",
        ]);
        break;

      case "waste_disposal_container_info":
        handleContainerBroadcast(wasteDisposal, patchData);
        break;

      case "waste_disposal_constituents":
        handleConstituentBroadcast(wasteDisposal, patchData);
        break;

      default:
        console.warn(
          `Unknown Waste Disposal broadcast patch_type: ${patch_type}`
        );
    }
  };

  return Object.freeze({
    base: {
      setActiveWasteDisposalTab: (tabName) => {
        h.setWasteDisposalUi((patch) => ({
          activeWasteDisposalTab: tabName,
        }));
      },
    },
    basicInfo: {
      ...makeWasteDisposalFormBasicInfoActions(h),
    },
    wasteInfo: {
      ...makeWasteDisposalFormWasteInfoActions(h),
    },
    paperWork: {
      ...makeWasteDisposalFormPaperWorkActions(h),
    },
    handleWasteDisposalBroadcast,
  });
};
